import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMachines } from '../context/MachineContext';
import { useWhatsApp } from '../hooks/useWhatsApp';
import { getCategoryLabel, getCategoryIcon } from '../utils';
import { COMPANY } from '../constants';
import MachineCard from '../components/MachineCard';
import styles from './MachineDetailPage.module.css';

function WaIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

export default function MachineDetailPage() {
  const { id } = useParams();
  const { getMachine, machines } = useMachines();
  const { openEnquiry } = useWhatsApp();
  const navigate = useNavigate();
  const machine = getMachine(id);

  const [activeImg, setActiveImg] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [lightbox, setLightbox] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  // All hooks must be before any early return
  const images = machine?.images ?? [];
  const closeLightbox = useCallback(() => setLightbox(false), []);
  const lbPrev = useCallback(() => setLightboxIdx(i => (i - 1 + images.length) % images.length), [images.length]);
  const lbNext = useCallback(() => setLightboxIdx(i => (i + 1) % images.length), [images.length]);

  // Auto-rotate images
  useEffect(() => {
    if (!machine?.images || machine.images.length <= 1 || !autoPlay) return;
    const t = setInterval(() => setActiveImg(i => (i + 1) % machine.images.length), 3000);
    return () => clearInterval(t);
  }, [machine, autoPlay]);

  // Keyboard nav for lightbox
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') lbPrev();
      if (e.key === 'ArrowRight') lbNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, closeLightbox, lbPrev, lbNext]);

  if (!machine) {
    return (
      <div className={styles.notFound}>
        <div className="container">
          <h2>Machine not found</h2>
          <p>This listing may have been removed.</p>
          <button onClick={() => navigate('/stock')} className={styles.backBtn}>← Back to Stock</button>
        </div>
      </div>
    );
  }

  const { title, brand, category, keyFeatures, description, featured } = machine;
  const similar = machines.filter(m => m.id !== id && m.category === category).slice(0, 3);
  const featureList = keyFeatures ? keyFeatures.split('|').map(f => f.trim()).filter(Boolean) : [];

  const openLightbox = (i) => { setLightboxIdx(i); setLightbox(true); };
  const handleThumbClick = (i) => { setActiveImg(i); setAutoPlay(false); };
  const prev = () => { setActiveImg(i => (i - 1 + images.length) % images.length); setAutoPlay(false); };
  const next = () => { setActiveImg(i => (i + 1) % images.length); setAutoPlay(false); };

  return (
    <>
    <div className={`${styles.page} page-wrapper`}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        <div className="container">
          <Link to="/" className={styles.breadLink}>Home</Link>
          <span className={styles.breadSep}>›</span>
          <Link to="/stock" className={styles.breadLink}>Stock</Link>
          <span className={styles.breadSep}>›</span>
          <span className={styles.breadCurrent}>{title}</span>
        </div>
      </div>

      <div className="container">
        <div className={styles.layout}>
          {/* ── LEFT: Image Gallery ───────────────────────────────────── */}
          <div className={styles.gallery}>
            <div className={styles.mainImgWrap}>
              {images?.length > 0 ? (
                <>
                  <div
                    className={styles.zoomContainer}
                    onClick={() => openLightbox(activeImg)}
                    title="Click to view fullscreen"
                    style={{ cursor: 'zoom-in' }}
                  >
                    <img
                      src={images[activeImg]}
                      alt={`${title} ${activeImg + 1}`}
                      className={styles.mainImg}
                    />
                  </div>
                  <div className={styles.zoomHint}>🔍 Click image to view fullscreen</div>
                </>
              ) : (
                <div className={styles.imgPlaceholder}>
                  <span style={{ fontSize: 64 }}>{getCategoryIcon(category)}</span>
                  <p style={{ color: '#5a6a75', marginTop: 8 }}>No images uploaded</p>
                </div>
              )}

              {images?.length > 1 && (
                <>
                  <button className={`${styles.navArrow} ${styles.navLeft}`} onClick={prev}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <button className={`${styles.navArrow} ${styles.navRight}`} onClick={next}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {images?.length > 1 && (
              <div className={styles.thumbRow}>
                {images.map((img, i) => (
                  <button
                    key={i}
                    className={`${styles.thumb} ${i === activeImg ? styles.thumbActive : ''}`}
                    onClick={() => handleThumbClick(i)}
                  >
                    <img src={img} alt={`${title} ${i + 1}`} />
                  </button>
                ))}
              </div>
            )}

            {/* Auto-play toggle */}
            {images?.length > 1 && (
              <button className={styles.autoPlayBtn} onClick={() => setAutoPlay(a => !a)}>
                {autoPlay ? '⏸ Pause slideshow' : '▶ Auto slideshow'}
              </button>
            )}
          </div>

          {/* ── RIGHT: Details ─────────────────────────────────────────── */}
          <div className={styles.details}>
            <div className={styles.catRow}>
              <span className={styles.catTag}>{getCategoryIcon(category)} {getCategoryLabel(category)}</span>
              {featured && <span className={styles.featuredTag}>⭐ Featured</span>}
            </div>

            <h1 className={styles.title}>{title}</h1>

            <div className={styles.metaRow}>
              {brand && <span className={styles.metaItem}><span className={styles.metaLabel}>Brand</span> {brand}</span>}
            </div>

            {/* Key Features */}
            {featureList.length > 0 && (
              <div className={styles.featuresBox}>
                <h3 className={styles.featuresTitle}>Key Features</h3>
                <ul className={styles.featuresList}>
                  {featureList.map((f, i) => (
                    <li key={i} className={styles.featureItem}>
                      <span className={styles.featureDot} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Description */}
            {description && (
              <div className={styles.descBox}>
                <h3 className={styles.descTitle}>Description</h3>
                <p className={styles.desc}>{description}</p>
              </div>
            )}

            {/* CTA Buttons */}
            <div className={styles.ctas}>
              <a href={`tel:${COMPANY.phone}`} className={styles.callPrimary}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
                Call to Enquire
              </a>
              <button className={styles.waPrimary} onClick={() => openEnquiry(machine)}>
                <WaIcon /> WhatsApp Enquiry
              </button>
              <p className={styles.ctaHint}>📞 {COMPANY.phone} · {COMPANY.hours}</p>
            </div>
          </div>
        </div>

        {/* Similar */}
        {similar.length > 0 && (
          <div className={styles.similar}>
            <h2 className={styles.similarTitle}>Similar Machines</h2>
            <div className={styles.similarGrid}>
              {similar.map(m => <MachineCard key={m.id} machine={m} />)}
            </div>
          </div>
        )}
      </div>
    </div>

      {/* ── LIGHTBOX ──────────────────────────────────────────────────── */}
      {lightbox && images?.length > 0 && (
        <div className={styles.lightboxOverlay} onClick={closeLightbox}>
          <button className={styles.lightboxClose} onClick={closeLightbox} aria-label="Close">&#x2715;</button>
          <button className={styles.lightboxPrev} onClick={(e) => { e.stopPropagation(); lbPrev(); }} aria-label="Previous">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div className={styles.lightboxImgWrap} onClick={e => e.stopPropagation()}>
            <img src={images[lightboxIdx]} alt={title} className={styles.lightboxImg} />
            <div className={styles.lightboxCounter}>{lightboxIdx + 1} / {images.length}</div>
          </div>
          <button className={styles.lightboxNext} onClick={(e) => { e.stopPropagation(); lbNext(); }} aria-label="Next">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          {images.length > 1 && (
            <div className={styles.lightboxDots}>
              {images.map((_, i) => (
                <button key={i} className={`${styles.lightboxDot} ${i === lightboxIdx ? styles.lightboxDotActive : ''}`}
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx(i); }} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
